import { createStackNavigator, HeaderStyleInterpolators } from "@react-navigation/stack";
import { useTheme } from "../../hooks/use-theme";
import { Platform } from "react-native";
import React from "react";
import ScreenHeader from "../../screens/user/ScreenHeader";
import { useI18n } from "../../hooks/i18n-hooks";
import Icon from "../../components/Icon";
import { useCardStyleInterpolator } from "../../hooks/use-card-style-interpolator";
import MissionDetailsScreen from "../../screens/user/MissionDetails";
import MissionTimeSheetScreen from "../../screens/user/MissionTimeSheetScreen";
import MissionsScreen from "../../screens/user/Missions";
import MissionClientFeesScreen from "../../screens/user/MissionClientFees";
import { ListItemProps } from "../../ui_atoms/ListItem";
import MissionInfoDeductionsScreen from "../../screens/user/MissionInfoDeductions";
import PayrollBreakdownListScreen from "../../screens/user/PayrollBreakdownListScreen";
import PayrollBreakdownDetailsScreen from "../../screens/user/PayrollBreakdownDetailsScreen";
import PayrollBreakdownTimeSheetScreen from "../../screens/user/PayrollBreakdownTimeSheetScreen";
import MissionLogsScreen from "../../screens/user/MissionLogs";

export type UserMissionsTabParamList = {
    MissionList: undefined;
    MissionDetails: { missionId: string };
    MissionClientFees: { fees: ({ key: string } & ListItemProps)[] };
    MissionInfoDeductions: { deductions: ({ key: string } & ListItemProps)[] };
    MissionTimeSheets: { missionId: string };
    MissionLogs: { missionId: string };
    PayrollBreakdownList: { missionId: string };
    PayrollBreakdownDetails: { payrollBreakdownId: string };
    PayrollBreakdownTimeSheetScreen: { payrollBreakdownId: string };
};

const Stack = createStackNavigator<UserMissionsTabParamList>();

const UserMissionsNavigation = () => {
    const theme = useTheme();
    const i18n = useI18n();

    const cardStyleInterpolator = useCardStyleInterpolator();

    return (
        <Stack.Navigator
            screenOptions={{
                headerStyle: {
                    backgroundColor: theme.colors.background,
                },
                headerMode: "screen",
                headerStatusBarHeight: 0,
                headerBackButtonDisplayMode: "minimal",
                headerBackImage: () => (
                    <Icon
                        name="back"
                        size={28}
                        style={{ marginLeft: Platform.OS === "ios" ? 10 : 0, marginRight: 5 }}
                    />
                ),
                headerTitleAlign: "center",
                gestureEnabled: true,
                headerShadowVisible: false,
                cardStyleInterpolator,
                headerStyleInterpolator: HeaderStyleInterpolators.forUIKit,
            }}
            initialRouteName="MissionList"
        >
            <Stack.Screen
                name="MissionList"
                component={MissionsScreen}
                options={{
                    title: i18n.t("screens.client_profile.tabs.missions.title"),
                    header: () => (
                        <ScreenHeader
                            label={i18n.t("screens.user.tabs.missions")}
                            useHeaderButton
                        />
                    ),
                }}
            />
            <Stack.Screen
                name="MissionDetails"
                component={MissionDetailsScreen}
                options={{
                    headerShown: false,
                    title: i18n.t("mission.nav_title"),
                    gestureEnabled: true,
                }}
            />
            <Stack.Screen
                name="MissionClientFees"
                component={MissionClientFeesScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("mission.details.client_fees"),
                    gestureEnabled: true,
                }}
            />
            <Stack.Screen
                name="MissionInfoDeductions"
                component={MissionInfoDeductionsScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("mission.details.mission_deductions"),
                    gestureEnabled: true,
                }}
            />
            <Stack.Screen
                name="MissionTimeSheets"
                component={MissionTimeSheetScreen}
                options={{
                    headerShown: true,
                    title: i18n.t("timesheet_list.title"),
                    gestureEnabled: true,
                }}
            />
            <Stack.Screen
                name="PayrollBreakdownList"
                component={PayrollBreakdownListScreen}
                options={{
                    headerShown: false,
                    gestureEnabled: true,
                }}
            />
            <Stack.Screen
                name="PayrollBreakdownDetails"
                component={PayrollBreakdownDetailsScreen}
                options={{
                    headerShown: false,
                    title: i18n.t("screens.payroll_breakdown.title"),
                    gestureEnabled: true,
                }}
            />
            <Stack.Screen
                name="PayrollBreakdownTimeSheetScreen"
                component={PayrollBreakdownTimeSheetScreen}
                options={{
                    headerShown: false,
                    title: i18n.t("screens.payroll_breakdown_timesheets.title"),
                    gestureEnabled: true,
                }}
            />
            <Stack.Screen
                name="MissionLogs"
                component={MissionLogsScreen}
                options={{
                    gestureEnabled: true,
                    header: () => (
                        <ScreenHeader
                            useSmallLabel
                            onLeftAction="back"
                            label={i18n.t("mission.history.title")}
                            hideProfile
                        />
                    ),
                    title: i18n.t("mission.history.title"),
                }}
            />
        </Stack.Navigator>
    );
};

export default UserMissionsNavigation;
